import { useCallback, useEffect, useRef, useState, type ButtonHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../basic/Button/Button';
import { Icon, type IconName } from '../../basic/Icon';
import styles from './ExportButton.module.css';

export interface ExportButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children' | 'type'> {
  /**
   * Ejecuta la descarga en Excel (p. ej. construir el blob y llamar a downloadBlob).
   * Puede ser asíncrona; mientras se resuelve se muestra el loader.
   */
  onExportExcel: () => void | Promise<void>;
  /**
   * Ejecuta la descarga en PDF. Si no se provee, la opción PDF se muestra
   * deshabilitada (seam BF-643/BF-644: el BE aún no expone el endpoint).
   */
  onExportPdf?: () => void | Promise<void>;
  /**
   * Fuerza el estado deshabilitado de la opción PDF aunque exista un handler.
   */
  pdfDisabled?: boolean;
  /**
   * Texto del botón. Por defecto "Descargar".
   */
  label?: string;
  /**
   * Texto de la opción Excel del menú contextual. Por defecto "Excel".
   */
  excelLabel?: string;
  /**
   * Texto de la opción PDF del menú contextual. Por defecto "PDF".
   */
  pdfLabel?: string;
  /**
   * Habilita el control según si hay resultados. Por defecto true.
   */
  hasResults?: boolean;
  /**
   * Ícono a la izquierda del texto. Por defecto 'download'. `null` lo oculta.
   */
  icon?: IconName | null;
  /**
   * Etiqueta accesible del menú contextual. Por defecto "Opciones de descarga".
   */
  menuAriaLabel?: string;
}

type ExportKind = 'excel' | 'pdf';

/**
 * Control de descarga de reportes.
 *
 * - Sin opción PDF: botón simple que descarga Excel (comportamiento previo).
 * - Con opción PDF (`onExportPdf` o `pdfDisabled`): botón "Descargar" que abre
 *   un menú contextual para elegir Excel o PDF.
 */
export function ExportButton({
  onExportExcel,
  onExportPdf,
  pdfDisabled = false,
  label = 'Descargar',
  excelLabel = 'Excel',
  pdfLabel = 'PDF',
  hasResults = true,
  icon = 'download',
  menuAriaLabel = 'Opciones de descarga',
  disabled,
  ...props
}: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [exporting, setExporting] = useState<ExportKind | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const showMenu = Boolean(onExportPdf) || pdfDisabled;
  const isDisabled = disabled || !hasResults;
  const isExporting = exporting !== null;
  const pdfItemDisabled = pdfDisabled || !onExportPdf || !hasResults;

  const close = useCallback(() => setIsOpen(false), []);

  const runExport = useCallback(
    async (kind: ExportKind, action?: () => void | Promise<void>) => {
      if (!action) return;
      setExporting(kind);
      try {
        await action();
      } finally {
        setExporting(null);
      }
    },
    [],
  );

  const handleTriggerClick = () => {
    if (!showMenu) {
      void runExport('excel', onExportExcel);
      return;
    }
    if (isDisabled) return;
    setIsOpen((prev) => !prev);
  };

  // Posicionar el menú debajo del trigger (arriba si no cabe), alineado a la derecha.
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !menuRef.current) return;

    const trigger = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const gap = 8;
    const safeArea = 16;

    let top = trigger.bottom + gap;
    if (top + menu.height + safeArea > window.innerHeight) {
      const above = trigger.top - menu.height - gap;
      top = above >= safeArea ? above : Math.max(safeArea, window.innerHeight - menu.height - safeArea);
    }

    let left = trigger.right - menu.width;
    if (left < safeArea) left = Math.max(safeArea, trigger.left);

    setPosition({ top, left });
  }, [isOpen]);

  // Cerrar al hacer click fuera.
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, close]);

  // Escape cierra el menú.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const menu =
    showMenu && isOpen
      ? createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            style={{ top: position.top, left: position.left }}
            role="menu"
            aria-label={menuAriaLabel}
          >
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              disabled={isExporting || !hasResults}
              onClick={() => {
                close();
                void runExport('excel', onExportExcel);
              }}
            >
              {excelLabel}
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              disabled={isExporting || pdfItemDisabled}
              onClick={() => {
                close();
                void runExport('pdf', onExportPdf);
              }}
            >
              {pdfLabel}
            </button>
          </div>,
          document.body,
        )
      : null;

  return (
    <span ref={triggerRef} className={styles.container}>
      <Button
        type="button"
        variant="outline"
        isLoading={isExporting}
        disabled={isDisabled}
        onClick={handleTriggerClick}
        aria-haspopup={showMenu ? 'menu' : undefined}
        aria-expanded={showMenu ? isOpen : undefined}
        {...props}
      >
        {icon && <Icon name={icon} size="sm" color="inherit" />}
        <span>{label}</span>
        {showMenu && (
          <svg
            className={styles.caret}
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1 1.5L6 6.5L11 1.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </Button>

      {menu}
    </span>
  );
}

ExportButton.displayName = 'ExportButton';
